import React, { useEffect, useMemo } from 'react';
import {
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Button,
  Flex,
  VStack,
  Link,
} from '@chakra-ui/react';
import { ExternalLinkIcon } from '@chakra-ui/icons';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import { useAuth } from 'src/utils/auth';
import { apiRequest } from 'src/utils/fetchUtils';
import { BasicCategory } from 'src/api/v1-api';
import { FormInputValues } from 'src/components/organisms/listing-form';
import { useState } from 'react';
import { hasValidPaymentForCategory } from 'src/utils/listingUtils';
import CancelSubscriptionButton from '../cancel-subscription-button';
import DeleteDraftConfirmationModal from 'src/components/organisms/listing-form/delete-listing-confirmation-modal';
import useSingleToast from 'src/utils/hooks/toast/useSingleToast';

interface Props {
  selectedCategory?: BasicCategory | null;
  listingId?: number | null;
  listing?: FormInputValues | null;
  submitLoading: boolean;
  setSubmitLoading: (loading: boolean) => void;
  submitDraftLoading: boolean;
  writingMode: 'Add' | 'Edit';
  handleSubmit: (e: any) => Promise<void>;
  isDisabled: boolean;
  onCancelSubscription: () => void;
}

const ListingFormSubmit = ({
  selectedCategory,
  listing,
  listingId,
  submitLoading,
  setSubmitLoading,
  submitDraftLoading,
  writingMode,
  handleSubmit,
  isDisabled,
  onCancelSubscription,
}: Props) => {
  const router = useRouter();
  const { getToken, isAuthenticated } = useAuth();
  const showToast = useSingleToast();
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState<boolean>(false);
  const { deleteDraft } = router.query;

  useEffect(() => {
    if (router.isReady && isAuthenticated && deleteDraft === 'true' && listing?.id) {
      setShowDeleteConfirmationModal(true);
    }
  }, [router.isReady, deleteDraft, isAuthenticated, listing?.id]);

  const isRejected = listing?.status === 'candidate_rejected';
  const inDraftEditingMode = listing?.status === 'draft' && listingId !== null;
  const inCreateMode = listing?.status === 'draft' && listingId === null;
  const saveButtonText = inDraftEditingMode
    ? 'Save Draft'
    : inCreateMode
    ? 'Save Draft'
    : 'Unpublish (Change to Draft)';
  const deleteButtonText = inDraftEditingMode ? 'Delete Draft' : 'Delete';

  const hasValidPayment = useMemo(
    () => hasValidPaymentForCategory(listing?.category_payments, selectedCategory?.id),
    [listing?.category_payments, selectedCategory?.id],
  );

  const hasActiveSubscription = useMemo(() => {
    return (
      hasValidPayment &&
      selectedCategory?.functional?.monthly_subscription &&
      selectedCategory?.functional?.requires_payment
    );
  }, [
    selectedCategory?.functional?.requires_payment,
    selectedCategory?.functional?.monthly_subscription,
    hasValidPayment,
  ]);

  const handleDelete = async () => {
    if (!listingId) return;

    setShowDeleteConfirmationModal(false);
    setDeleteLoading(true);
    const url = `${process.env.NEXT_PUBLIC_API_HOST}/listings/${listingId}/delete/`;
    const token = await getToken();
    if (!token) {
      const returnUrl = `/listing/edit/${listingId}`;
      router.push(`/login?returnUrl=${encodeURIComponent(returnUrl)}`);
      return;
    }
    try {
      await apiRequest('DELETE', url, token);
      router.push('/?success=true&message=Listing+deleted');
    } catch (err: any) {
      showToast({
        title: 'Error deleting listing',
        description: 'Unable to delete listing. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        sentryAlert: {
          error: new Error('Error deleting listing'),
          level: 'error',
          extras: {
            listingId: listingId.toString(),
          },
        },
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  let publishButtonText = 'Publish';

  if (listingId) {
    if (isRejected) {
      publishButtonText = 'Re-Submit for Approval';
    } else {
      publishButtonText = 'Save';
    }
  } else if (selectedCategory?.functional?.requires_approval) {
    publishButtonText = 'Submit for Approval';
  } else if (!selectedCategory) {
    publishButtonText = 'Select Category Before Saving';
  }

  if (writingMode === 'Edit' && listing?.status === 'draft') {
    publishButtonText = 'Publish';
  }

  if (selectedCategory?.functional?.requires_payment) {
    if (!hasValidPayment) {
      publishButtonText = selectedCategory?.functional?.pay_to_list?.cta?.text || 'Pay and Publish';
    }
  }

  const publishButton = (
    <Button
      isLoading={submitLoading}
      colorScheme="primary"
      variant="solid"
      data-action="publish"
      type="submit"
      isDisabled={(!isAuthenticated && writingMode === 'Edit') || isDisabled || !selectedCategory}
      data-test="publish-btn"
    >
      {publishButtonText}
    </Button>
  );

  const paymentInfoBox = () => {
    if (!selectedCategory?.functional?.requires_payment || hasValidPayment) return null;

    // @ts-ignore
    const alertStyle = selectedCategory?.functional?.pay_to_list?.info?.style ?? 'info';
    // @ts-ignore
    const alertTitle = selectedCategory?.functional?.pay_to_list?.info?.title ?? 'Payment Required';

    const alertDescription =
      // @ts-ignore
      selectedCategory?.functional?.pay_to_list?.info?.text ??
      'After clicking Publish you will be redirected to a payment page. Your listing will be live soon after payment is received.';

    return (
      <>
        <Alert status={alertStyle} variant="left-accent">
          <AlertIcon />
          <AlertTitle mr={2}>{alertTitle}</AlertTitle>
          <AlertDescription>{alertDescription}</AlertDescription>
        </Alert>
      </>
    );
  };

  return (
    <VStack alignItems="flex-end" spacing={4}>
      {paymentInfoBox()}

      <Flex alignItems="center" justifyContent="flex-end" flexWrap="wrap" gap={4}>
        {listingId && (
          <>
            {hasActiveSubscription && (
              <CancelSubscriptionButton
                cancellationRequested={listing?.sub_status === 'cancellation_requested'}
                onCancelSubscription={onCancelSubscription}
              />
            )}
            <Button
              colorScheme="red"
              variant="outline"
              onClick={() => setShowDeleteConfirmationModal(true)}
              isDisabled={!isAuthenticated || isDisabled}
              data-test="listing-delete-btn"
              isLoading={deleteLoading}
            >
              {deleteButtonText}
            </Button>
            <Button colorScheme="primary" variant="outline">
              <NextLink href={`/listing/${listingId}`}>View Listing</NextLink>
              <Link as={NextLink} href={`/listing/${listingId}`} isExternal>
                <ExternalLinkIcon marginBottom={1} marginLeft={1} />
              </Link>
            </Button>
          </>
        )}
        {!!selectedCategory && (
          <Button
            isLoading={submitDraftLoading}
            colorScheme="primary"
            variant="outline"
            data-action="submitDraft"
            type="submit"
            isDisabled={(!isAuthenticated && writingMode === 'Edit') || isDisabled}
            onClick={handleSubmit}
            data-test="save-draft-btn"
          >
            {saveButtonText}
          </Button>
        )}
        {publishButton}
      </Flex>
      <DeleteDraftConfirmationModal
        isOpen={showDeleteConfirmationModal}
        onClose={() => setShowDeleteConfirmationModal(false)}
        onConfirm={handleDelete}
      />
    </VStack>
  );
};

export default ListingFormSubmit;
